/**
 * Sincronização de identidade de sessão entre abas/janelas do mesmo origin.
 * BroadcastChannel + localStorage (evento `storage` noutras abas).
 */

export const AUTH_SYNC_CHANNEL = "spm-auth-sync";
export const AUTH_SYNC_STORAGE_KEY = "spm_auth_sync";

/** Id estável desta aba (não exportar mutável em testes — usar getAuthSyncTabId). */
let tabId = "";

export function getAuthSyncTabId() {
  if (tabId) return tabId;
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    tabId = crypto.randomUUID();
  } else {
    tabId = `t-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return tabId;
}

/** Só para testes. */
export function resetAuthSyncTabIdForTests(next = "") {
  tabId = next;
}

/**
 * Chave estável da identidade (comparar antes de recarregar).
 * @param {{ id_user?: unknown, nm_login?: unknown, cd_alias?: unknown } | null | undefined} user
 */
export function authIdentityKey(user) {
  if (!user) return "";
  const id = user.id_user != null ? String(user.id_user) : "";
  const login = String(user.nm_login || "").trim().toLowerCase();
  const alias = String(user.cd_alias || "")
    .trim()
    .toLowerCase();
  if (!id && !login) return "";
  return `${id}:${login}:${alias}`;
}

/**
 * @param {{ id_user?: unknown, nm_login?: unknown, cd_alias?: unknown } | null | undefined} user
 */
export function buildAuthSyncPayload(user) {
  return {
    v: 1,
    tabId: getAuthSyncTabId(),
    at: Date.now(),
    identity: authIdentityKey(user),
    loggedIn: Boolean(user && authIdentityKey(user)),
  };
}

/**
 * Anuncia mudança de sessão às outras abas. Não usa sessionStorage (por-aba).
 * @param {{ id_user?: unknown, nm_login?: unknown, cd_alias?: unknown } | null | undefined} user
 */
export function broadcastAuthChange(user) {
  if (typeof window === "undefined") return;
  const payload = buildAuthSyncPayload(user);
  const raw = JSON.stringify(payload);
  try {
    if (typeof BroadcastChannel !== "undefined") {
      const ch = new BroadcastChannel(AUTH_SYNC_CHANNEL);
      ch.postMessage(payload);
      ch.close();
    }
  } catch {
    /* ignore */
  }
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(AUTH_SYNC_STORAGE_KEY, raw);
    }
  } catch {
    /* ignore */
  }
}

/**
 * @param {(payload: ReturnType<typeof buildAuthSyncPayload>) => void} onRemoteChange
 * @param {{ debounceMs?: number }} [opts]
 * @returns {() => void} unsubscribe
 */
export function subscribeAuthChange(onRemoteChange, opts = {}) {
  if (typeof window === "undefined") return () => {};
  const debounceMs = opts.debounceMs ?? 400;
  let lastAt = 0;
  let timer = null;

  const handle = (payload) => {
    if (!payload || typeof payload !== "object") return;
    if (payload.tabId && payload.tabId === getAuthSyncTabId()) return;
    const at = typeof payload.at === "number" ? payload.at : Date.now();
    if (at && lastAt && at <= lastAt) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      lastAt = at || Date.now();
      onRemoteChange(payload);
    }, debounceMs);
  };

  let channel = null;
  try {
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
      channel.onmessage = (ev) => handle(ev?.data);
    }
  } catch {
    channel = null;
  }

  const onStorage = (ev) => {
    if (ev.key !== AUTH_SYNC_STORAGE_KEY || !ev.newValue) return;
    try {
      handle(JSON.parse(ev.newValue));
    } catch {
      /* ignore */
    }
  };
  window.addEventListener("storage", onStorage);

  return () => {
    if (timer) clearTimeout(timer);
    window.removeEventListener("storage", onStorage);
    try {
      channel?.close();
    } catch {
      /* ignore */
    }
  };
}
